import React, { useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  IconButton,
} from "@mui/material";
import { useLoading } from "../../contexts/LoadingContext";
import Modal from "../Modal/Modal";
import Button from "../Button/Button";
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  HeaderContainer,
  ButtonGroup,
  IconContainer,
} from "./FormValidationStyles";
import CloseIcon from "@mui/icons-material/Close";
import { createFieldValidator } from "../../utils/validationHelper";
import { renderFormField } from "../../utils/formFieldRenderer";
import { useRbac } from "../../hooks/RbacContext";

const FormValidation = ({
  open,
  onClose,
  initialData,
  onSubmit,
  formFields,
  title,
  icon: Icon,
  getOptionsForField,
  isEdit,
  modelWidth,
  moduleName,
  onDependencyChange,
}) => {
  const [errorModal, setErrorModal] = useState(null);
  const { showLoading, hideLoading } = useLoading();
  const { permissions } = useRbac();

  const validationSchema = Yup.object().shape(
    Object.entries(formFields).reduce((acc, [key, field]) => {
      acc[key] = createFieldValidator(field, formFields);
      return acc;
    }, {})
  );

  const transformInitialValues = (data) => {
    const transformed = { ...data };
    Object.entries(formFields).forEach(([fieldName, fieldConfig]) => {
      if (fieldConfig.dynamic && fieldConfig.fields) {
        // Handle POC data structure
        if (
          data[fieldName] &&
          typeof data[fieldName] === "object" &&
          !Array.isArray(data[fieldName])
        ) {
          // Get the length of any array in the POC data to know how many sets to create
          const pocArrayLength = Object.values(data[fieldName])[0]?.length || 0;

          // Initialize empty arrays for each field
          transformed[fieldName] = {};
          Object.keys(fieldConfig.fields).forEach((subFieldName) => {
            transformed[fieldName][subFieldName] =
              data[fieldName][subFieldName] || // Use existing data if available
              Array(pocArrayLength).fill(""); // Otherwise create empty array of same length
          });
        } else {
          // Default initialization for new forms
          transformed[fieldName] = {};
          Object.keys(fieldConfig.fields).forEach((subFieldName) => {
            transformed[fieldName][subFieldName] = [""];
          });
        }
      }
    });
    return transformed;
  };

  const transformSubmitData = (values) => {
    const transformed = { ...values };
    Object.entries(formFields).forEach(([fieldName, fieldConfig]) => {
      if (fieldConfig.dynamic && fieldConfig.fields) {
        transformed[fieldName] = {};
        Object.keys(fieldConfig.fields).forEach((subFieldName) => {
          transformed[fieldName][subFieldName] =
            values[fieldName]?.[subFieldName]?.filter((val) => val !== "") ||
            [];
        });
      }
    });
    return transformed;
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      showLoading();
      const transformedData = transformSubmitData(values);
      console.log("transformedDatatransformedData", transformedData);
      const result = await onSubmit(transformedData);
      console.log("resultresult", result);
      if (result && typeof result === "object") {
        if (result.success) {
          resetForm();
          onClose();
        } else {
          setErrorModal({
            type: "error",
            title: "Action Failed",
            message: result.error || "An error occurred while saving the data",
          });
        }
      } else {
        // resetForm();
        // onClose();
      }
    } catch (error) {
      setErrorModal({
        type: "error",
        title: "Action Failed",
        message: error?.message || "An unexpected error occurred",
      });
    } finally {
      hideLoading();
      setSubmitting(false);
    }
  };

  const handleErrorModalClose = () => {
    setErrorModal(null);
  };

  const handleCustomChange = (
    fieldName,
    value,
    setFieldValue,
    setFieldTouched
  ) => {

    if (fieldName === "tag_id" && Array.isArray(value)) {
      const allOption = "all";
      // If user selects 'All', only keep 'all' in the value
      if (value.includes(allOption)) {
        setFieldValue(fieldName, [allOption]);
        setFieldTouched(fieldName, true);
        return;
      }
      // If user selects any other tag, remove 'all' from the value
      if (value.length > 1 && value.includes(allOption) === false) {
        setFieldValue(fieldName, value.filter(v => v !== allOption));
        setFieldTouched(fieldName, true);
        return;
      }
    }

    setFieldValue(fieldName, value);
    setFieldTouched(fieldName, true);

    const fieldConfig = formFields[fieldName];
    if (fieldConfig) {
      Object.entries(formFields).forEach(([field, config]) => {
        if (config.dependsOn?.field === fieldName) {
          if (config.dependsOn.clearOnChange) {
            setFieldValue(field, "");
            setFieldTouched(field, false);
          }
        }
      });

      if (fieldConfig.affects) {
        fieldConfig.affects.forEach((affectedField) => {
          setFieldValue(affectedField, "");
          setFieldTouched(affectedField, false);
        });
      }

      if (onDependencyChange) {
        onDependencyChange(fieldName, value, setFieldValue);
      }
    }
  };

  return (
    <>
      <StyledDialog maxWidth={modelWidth} open={open} fullWidth>
        <StyledDialogTitle>
          <HeaderContainer>
            <IconContainer>
              {Icon && <Icon />}
              <Typography variant="h6">
                {isEdit ? `${title}` : `Add New ${title}`}
              </Typography>
            </IconContainer>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </HeaderContainer>
        </StyledDialogTitle>

        <Formik
          initialValues={transformInitialValues(initialData || {})}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
          validateOnMount={false}
          validateOnChange={false}
          validateOnBlur={true}
        >
          {({
            values,
            errors,
            touched,
            handleChange: formikHandleChange,
            handleBlur,
            setFieldValue,
            setFieldTouched,
            resetForm,
            isSubmitting,
          }) => {
            // console.log("errors", errors);
            return (
              <Form noValidate>
                <div style={{ display: "none" }}>
                  <input type="text" name="fake_user" autoComplete="username" />
                  <input
                    type="password"
                    name="fake_pass"
                    autoComplete="current-password"
                  />
                </div>
                <StyledDialogContent>
                  <Grid
                    container
                    spacing={1.5}
                    sx={{
                      width: "100%",
                      m: 0,
                    }}
                  >
                    {Object.entries(formFields).map(
                      ([fieldName, fieldConfig]) => {
                        let fieldOptions = [];

                        if (fieldConfig.source) {
                          fieldOptions = getOptionsForField(fieldConfig.source);
                        } else if (fieldConfig.fields) {
                          fieldOptions = Object.entries(
                            fieldConfig.fields
                          ).reduce((acc, [subFieldName, subFieldConfig]) => {
                            if (subFieldConfig.source) {
                              acc[subFieldConfig.source] = getOptionsForField(
                                subFieldConfig.source
                              );
                            }
                            return acc;
                          }, {});
                        }

                        const fieldElement = renderFormField({
                          fieldName,
                          fieldConfig,
                          formData: values,
                          errors,
                          touched,
                          handleChange: (fieldName, value) => {
                            handleCustomChange(
                              fieldName,
                              value,
                              setFieldValue,
                              setFieldTouched
                            );
                          },
                          handleBlur,
                          setFieldValue,
                          setFieldTouched,
                          options: fieldOptions,
                          isEdit,
                          moduleName,
                          permissions,
                        });
                        return fieldElement != null
                          ? React.cloneElement(fieldElement, { key: fieldName })
                          : null;
                      }
                    )}
                  </Grid>

                  <ButtonGroup>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        resetForm();
                        onClose();
                      }}
                      size="small"
                      fullWidth
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="small"
                      fullWidth
                    >
                      {`Save`}
                    </Button>
                  </ButtonGroup>
                </StyledDialogContent>
              </Form>
            );
          }}
        </Formik>
      </StyledDialog>

      {errorModal && (
        <Modal
          type={errorModal.type}
          title={errorModal.title}
          message={errorModal.message}
          onClose={handleErrorModalClose}
        />
      )}
    </>
  );
};

export default FormValidation;
